/**
 * Módulo Vuex para la gestión de la información de la empresa.
 *
 * Este módulo maneja la configuración de monedas, formato de fecha, datos de la
 * empresa, y colores corporativos, almacenando y recuperando información desde
 * localStorage.
 */

export default {
  namespaced: true,

  /**
   * Estado inicial del módulo.
   * - currencies: Lista de monedas disponibles.
   * - default_currency: Moneda por defecto de la empresa.
   * - companyData: Información codificada de la empresa.
   * - dateFormatDefault: Formato de fecha por defecto.
   */
  state: {
    currencies:
      localStorage.getItem('currencies') == undefined
        ? []
        : JSON.parse(localStorage.getItem('currencies')),
    default_currency:
      localStorage.getItem('currency') == undefined
        ? {
            style: 'currency',
            locale: 'en-US',
            currency: 'USD'
          }
        : JSON.parse(localStorage.getItem('currency')),
    companyData: localStorage.getItem('company'),
    dateFormatDefault: localStorage.getItem('dateFormat')
  },

  /**
   * Getters para acceder a los datos del estado de forma procesada.
   */
  getters: {
    /**
     * Filtra las monedas por nombre o código.
     * @param {Object} state - Estado del módulo.
     * @param {String} currency - Texto a buscar en nombre o código de la moneda.
     * @returns {Array} Lista de monedas filtradas.
     */
    getCurrencies: (state) => (currency) => {
      return state.currencies.filter((m) => {
        if (currency == undefined || currency == '') {
          return m
        } else if (
          m.name.toUpperCase().indexOf(currency.toUpperCase()) != -1 ||
          m.code.toUpperCase().indexOf(currency.toUpperCase()) != -1
        ) {
          return m
        }
      })
    },

    /**
     * Obtiene la moneda por defecto.
     * @param {Object} state - Estado del módulo.
     * @returns {Object} Moneda por defecto.
     */
    getDefaultCurrency: (state) => {
      return state.default_currency
    },

    /**
     * Obtiene el formato de fecha por defecto.
     * @param {Object} state - Estado del módulo.
     * @returns {String} Formato de fecha.
     */
    getdateFormatDefault: (state) => state.dateFormatDefault,

    /**
     * Obtiene el logo de la empresa.
     * @param {Object} state - Estado del módulo.
     * @param {Object} getters - Getters del módulo.
     * @returns {String|undefined} URL de la imagen del logo.
     */
    getLogo: (state, getters) =>
      getters.getCompanyData ? getters.getCompanyData.company_photo : undefined,

    /**
     * Decodifica y obtiene los datos de la empresa.
     * @param {Object} state - Estado del módulo.
     * @returns {Object|undefined} Datos de la empresa.
     */
    getCompanyData: (state) =>
      typeof state.companyData === 'string'
        ? JSON.parse(atob(state.companyData))
        : undefined,

    /**
     * Obtiene los colores corporativos de la empresa.
     * @param {Object} state - Estado del módulo.
     * @param {Object} getters - Getters del módulo.
     * @returns {Object} Colores primario y secundario.
     */
    getColors: (state, getters) => ({
      primary:
        getters.getCompanyData === undefined
          ? '#34495e'
          : getters.getCompanyData.primary_color,
      secondary:
        getters.getCompanyData === undefined
          ? '#345d48'
          : getters.getCompanyData.secondary_color
    })
  },

  /**
   * Mutaciones para modificar el estado del módulo.
   */
  mutations: {
    /**
     * Actualiza la lista de monedas y la guarda en localStorage.
     * @param {Object} state - Estado del módulo.
     * @param {Array} newCurrencies - Nueva lista de monedas.
     */
    setCurrencies(state, newCurrencies) {
      state.currencies = newCurrencies
      localStorage.setItem('currencies', JSON.stringify(newCurrencies))
    },

    /**
     * Actualiza la moneda por defecto y la guarda en localStorage.
     * @param {Object} state - Estado del módulo.
     * @param {Object} currency - Nueva moneda por defecto.
     */
    setDefaultCurrency(state, currency) {
      state.default_currency = currency
      localStorage.setItem('currency', JSON.stringify(currency))
    },

    /**
     * Actualiza los datos de la empresa, el formato de fecha y la moneda por
     * defecto.
     *
     * Guarda los datos en localStorage.
     * @param {Object} state - Estado del módulo.
     * @param {Object} company - Datos de la empresa.
     */
    companyData(state, company) {
      let coded = btoa(JSON.stringify(company))
      let default_currency = {
        locale: company.country.lang,
        style: 'currency',
        currency: company.currency.code
      }
      state.companyData = coded
      state.dateFormatDefault = company.format_date
      state.default_currency = default_currency
      localStorage.setItem('company', coded)
      localStorage.setItem('dateFormat', company.format_date)
      localStorage.setItem('currency', JSON.stringify(default_currency))
    },
    /**
     * Resetea los datos de la empresa y limpia localStorage.
     * @param {Object} state - Estado del módulo.
     */
    resetCompany(state) {
      state.companyData = null
      state.dateFormatDefault = null
      state.currencies = []
      state.default_currency = {
        style: 'currency',
        locale: 'en-US',
        currency: 'USD'
      }
      localStorage.removeItem('company')
      localStorage.removeItem('dateFormat')
      localStorage.removeItem('currencies')
      localStorage.removeItem('currency')
    }
  },

  /**
   * Acciones para ejecutar mutaciones de forma asíncrona o desde componentes.
   */
  actions: {
    /**
     * Acción para resetear los datos de la empresa.
     * @param {Function} commit - Función commit de Vuex.
     */
    resetCompany({ commit }) {
      commit('resetCompany')
    },

    /**
     * Acción para actualizar la moneda por defecto.
     * @param {Function} commit - Función commit de Vuex.
     * @param {Object} currency - Nueva moneda por defecto.
     */
    setDefaultCurrency({ commit }, currency) {
      commit('setDefaultCurrency', currency)
    },

    /**
     * Acción para actualizar la lista de monedas.
     * @param {Function} commit - Función commit de Vuex.
     * @param {Array} currencies - Nueva lista de monedas.
     */
    setCurrencies({ commit }, currencies) {
      commit('setCurrencies', currencies)
    }
  }
}
